import {
  ButtonProps,
  ButtonVariant,
  getActiveBackgroundColor,
  getBackgroundColor,
  getBorder,
  getDropShadow,
  getFillColor,
  getFontColor,
  getFontSize,
  getGapSize,
  getHoverBackgroundColor,
  getHoverDropShadow,
  getHoveredBorder,
  getHoverFontColor,
  getPadding,
  getSvgSize,
} from "@src/components/elements/ButtonVariants";
import React, { forwardRef, MouseEventHandler, ReactNode } from "react";
import styled from "styled-components/macro";
import { Spinner } from "./Spinner";

const StyledButton = styled.button<ButtonProps>`
  background-color: ${(props) =>
    props.active ? getHoverBackgroundColor(props) : getBackgroundColor(props)};
  color: ${(props) => getFontColor(props)};
  fill: ${(props) => getFillColor(props)};
  font-family: var(--inter-font);
  font-size: ${(props) => getFontSize(props)};
  font-weight: var(--font-weight-bold);
  border-radius: 0.25em;
  border: ${(props) => getBorder(props)};
  padding: ${(props) => getPadding(props)};
  cursor: ${(props) => (props.disabled ? "default" : "pointer")};
  transition: 200ms;
  width: ${({ fullWidth }) => (fullWidth ? "100%" : "fit-content")};
  white-space: nowrap;
  box-shadow: ${(props) => getDropShadow(props)};
  animation: scale-from-small 0.3s ease-out;

  &:hover {
    background-color: ${(props) => getHoverBackgroundColor(props)};
    color: ${(props) => getHoverFontColor(props)};
    border: ${(props) => getHoveredBorder(props)};
    box-shadow: ${(props) => getHoverDropShadow(props)};
  }

  &:active,
  &:focus {
    background-color: ${(props) => getActiveBackgroundColor(props)};
  }

  svg {
    width: ${(props) => getSvgSize(props)};
    height: ${(props) => getSvgSize(props)};
    fill: ${(props) => getFillColor(props)};
  }
  .button-content,
  .button-content span {
    display: flex;
    gap: ${(props) => getGapSize(props)};
    align-items: center;
    justify-content: center;
    vertical-align: middle;
    ${({ nowrap }) => (nowrap ? "white-space: nowrap;" : "")}
  }

  @media screen and (max-width: 40rem) {
    span.hide-text-mobile {
      display: none;
    }
  }
`;

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      onClick,
      style,
      type,
      className,
      children,
      loading,
      disabled,
      variant,
      size,
      active,
      title,
      nowrap,
      fullWidth,
      ...rest
    },
    ref,
  ) => {
    const onClickFn: MouseEventHandler<HTMLButtonElement> = (e) => {
      !disabled && onClick && onClick(e);
    };

    return (
      <StyledButton
        style={style}
        className={className}
        type={type || "button"}
        variant={variant || "primary"}
        size={size || "medium"}
        active={active || false}
        disabled={loading || disabled}
        onClick={onClickFn}
        title={title}
        nowrap={nowrap}
        fullWidth={fullWidth}
        ref={ref}
        {...rest}
      >
        <div className="button-content">
          <span>{children}</span>
          {loading ? <Spinner size="small" /> : null}
        </div>
      </StyledButton>
    );
  },
);

const StyledButtonGroup = styled.div<{
  variant?: Pick<ButtonProps, "variant">;
}>`
  display: flex;
  border-radius: var(--rounded-corner);

  > * {
    /* when there is more than 1 child, the leftmost el will not have a right border radius, and the rightmost el will not have a left border radius */
    &:first-child:not(:last-child) {
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
    }

    &:last-child:not(:first-child) {
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
    }

    /* all the inner children will have straight borders */
    &:not(:first-child):not(:last-child) {
      border-radius: 0;
    }
  }
`;

export const ButtonGroup = ({
  variant = "primary",
  children,
  className,
}: {
  variant?: ButtonVariant;
  children: ReactNode;
  className?: string;
}) => {
  const clones = React.Children.map(children, (child) => {
    if (React.isValidElement(child)) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return React.cloneElement(child, { variant } as any);
    }
    return child;
  });
  return <StyledButtonGroup className={className}>{clones}</StyledButtonGroup>;
};

Button.displayName = "Button";
